<script lang="ts" setup>
  import { cn } from "@/lib/utils"
  import { StepperItem, useForwardProps, type StepperItemProps } from "reka-ui"
  import { computed, type HTMLAttributes } from "vue"
  import {
    StepperIndicator,
    StepperSeparator,
    StepperTrigger,
  } from "@/components/ui/stepper"

  import { StepperItemState, StepperItemStateType } from "./StepperTypes"

  const props = withDefaults(
    defineProps<
      StepperItemProps & {
        class?: HTMLAttributes["class"]
        stepCount: number
        isCompleted?: boolean
        isAnimated?: boolean
        state?: StepperItemStateType
      }
    >(),
    {
      isCompleted: false,
      isAnimated: false,
      state: StepperItemState.IDLE,
    },
  )

  // Steps count from 1, as in reka.
  const stepSeparatorPosition = computed(() => {
    switch (props.step) {
      case 1:
        return "start"
      case props.stepCount - 1:
        return "last"
      default:
        return ""
    }
  })

  const isLastStep = computed(() => props.step === props.stepCount)

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardProps(delegatedProps)
</script>

<template>
  <StepperItem
    v-bind="forwarded"
    :data-step-state="state"
    :class="
      cn(
        'group pointer-events-none relative',
        'flex flex-col items-start',
        'sm:w-full sm:items-center  sm:justify-start',
        props.class,
      )
    "
  >
    <StepperSeparator
      v-if="!isLastStep"
      :position="stepSeparatorPosition"
      :is-completed="isCompleted"
    />
    <StepperTrigger
      class="flex flex-row items-start sm:max-w-[132px] sm:flex-col sm:items-center"
    >
      <StepperIndicator :state="state" :is-animated="isAnimated" />
      <div :class="cn('ml-2 mt-1 text-left sm:m-0 sm:text-center')">
        <slot />
      </div>
    </StepperTrigger>
    <!-- Outside the trigger, which is a button, so it can hold interactive content. -->
    <slot name="after" />
  </StepperItem>
</template>
